// Centralised theme application. Reads appearance settings and applies them as
// CSS custom properties on :root so the whole app restyles without a rebuild.

export const FONTS = [
  { label: 'Poppins', value: 'Poppins', google: 'Poppins:wght@300;400;500;600;700' },
  { label: 'Roboto', value: 'Roboto', google: 'Roboto:wght@300;400;500;700' },
  { label: 'Open Sans', value: 'Open Sans', google: 'Open+Sans:wght@300;400;500;600;700' },
  { label: 'Montserrat', value: 'Montserrat', google: 'Montserrat:wght@300;400;500;600;700' },
  { label: 'Inter', value: 'Inter', google: 'Inter:wght@300;400;500;600;700' },
  { label: 'Lato', value: 'Lato', google: 'Lato:wght@300;400;700' },
  { label: 'Nunito', value: 'Nunito', google: 'Nunito:wght@300;400;500;600;700' },
  { label: 'Source Sans 3', value: 'Source Sans 3', google: 'Source+Sans+3:wght@300;400;500;600;700' }
];

const FONT_MAP = FONTS.reduce((acc, f) => { acc[f.value] = f; return acc; }, {});

let _loadedFonts = new Set();

export function loadGoogleFont(fontFamily) {
  const font = FONT_MAP[fontFamily];
  if (!font) return;
  if (_loadedFonts.has(fontFamily)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${font.google}&display=swap`;
  link.dataset.font = fontFamily;
  document.head.appendChild(link);
  _loadedFonts.add(fontFamily);
}

export function applyTheme(settings) {
  const theme = (settings && settings.theme) || {};
  const root = document.documentElement;

  if (theme.primaryColor) {
    root.style.setProperty('--primary-color', theme.primaryColor);
    root.style.setProperty('--primary-dark', shade(theme.primaryColor, -15));
    root.style.setProperty('--primary-light', shade(theme.primaryColor, 40));
  }

  if (theme.secondaryColor) {
    root.style.setProperty('--secondary-color', theme.secondaryColor);
  }

  if (theme.borderRadius) {
    root.style.setProperty('--border-radius', `${theme.borderRadius}px`);
  }

  const fontFamily = theme.font || 'Poppins';
  root.style.setProperty('--app-font', `'${fontFamily}', sans-serif`);
  loadGoogleFont(fontFamily);

  if (theme.themeName) {
    root.setAttribute('data-theme', theme.themeName);
  }
}

// Re-apply a user's saved light/dark preference (overrides settings themeName).
export function initTheme() {
  const saved = localStorage.getItem('theme');
  if (saved === 'dark' || saved === 'light') {
    document.documentElement.setAttribute('data-theme', saved);
  }
}

export function toggleTheme() {
  const root = document.documentElement;
  const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  root.setAttribute('data-theme', next);
  try { localStorage.setItem('theme', next); } catch { /* ignore */ }
  return next;
}

// Lighten/darken a hex color by percent (-100..100)
function shade(hex, percent) {
  try {
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map((x) => x + x).join('');
    const num = parseInt(c, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    const t = percent < 0 ? 0 : 255;
    const p = Math.abs(percent) / 100;
    r = Math.round((t - r) * p) + r;
    g = Math.round((t - g) * p) + g;
    b = Math.round((t - b) * p) + b;
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch (e) {
    return hex;
  }
}

export default { FONTS, applyTheme, loadGoogleFont };
